import { useQuery } from '@tanstack/react-query';
import { location } from '@cwos/api-client';

import { Pill, Refused } from '@cwos/ui-admin';
import { formatScheduleInstant } from '../location-settings/copy.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { ListState } from '../ListStates.js';
import { listPhase } from '../listState.js';
import { usePermissions } from '../permissions.js';
import {
  basisSentence,
  completenessSentence,
  disclosureHeading,
  disclosureSentence,
  floorLabel,
  pendingArrivalSentence,
  statedScheduleSentence,
} from './policyBinding.js';

/**
 * WHAT THIS SHIFT WAS BOUND TO — the founder's preservation clause, on a screen.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE RULING THIS DISCHARGES (2026-09-08)
 * ═══════════════════════════════════════════════════════════════════════════
 *   "Preserve the policy version/effective timestamp associated with the shift
 *    so Phibian can later establish what the worker was told and what collection
 *    rules governed that shift."
 *
 *   "`effective_from` must therefore be real domain behaviour, not dead storage."
 *
 * `GET /location/sessions/:id/policy-binding` has answered that in full since
 * migration 1962000000000 — the version a session was bound to, its instants,
 * the operator's own stated schedule, and the replayed timeline of what actually
 * governed collection — and until this component NO screen in either app read
 * it. A binding nobody can read is dead storage with extra steps, so this is
 * the ruling's own evidence rather than a report about it.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY IT LIVES ON SESSION DETAIL AND NOT IN SETTINGS
 * ═══════════════════════════════════════════════════════════════════════════
 * The question is "what was THIS worker told at the start of THIS shift, and
 * what governed it" — a fact about one recorded session, next to that session's
 * punches, its verification states and its coverage. The organization's
 * forward-looking question ("when does the change I just saved begin") is a
 * different one and already has a surface on the Location & geofencing hub.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * A SECOND KEY ON A SCREEN THAT ALREADY HAS ONE, AND WHY THAT IS NOT A BUG
 * ═══════════════════════════════════════════════════════════════════════════
 * Session detail is gated on `admin-views:read`. This read is NOT: the route
 * charges `time:read` plus own-worker scoping, deliberately — it returns policy
 * CONFIGURATION and no coordinates, and its route comment says the first person
 * entitled to the answer is the worker themselves. The two keys are granted
 * separately (1755220000000 gives `admin-views:read` to org_admin and supervisor
 * and records that a worker gets none; 1755064800000 gives `time:read` to all
 * three), so "reached this screen" does not imply "may read this panel".
 *
 * `authorityGate` AND NOT `!has(BINDING_READ_KEY)`, which is the whole of the
 * unknown-is-not-denied rule and is not a style preference. `has()` resolves an
 * in-flight and an unreadable permission set to `false` along with a genuine
 * refusal, so a bare negation renders "you do not hold this" at every reader on
 * every cold load — an accusation, made from an answer that has not arrived.
 * Four states, four renderings: `unresolved` says it is checking, `unknown`
 * falls THROUGH to the request and lets the server answer for itself, `refused`
 * is the only one entitled to name the missing key, and `allowed` asks.
 *
 * `has` and not `can`: this is question 1 in `permissions.ts`'s vocabulary — may
 * the panel be revealed — and a program-scoped grant is a real grant the server
 * may well honour for a session inside that program. The server owns the scope
 * decision, because it knows the supervision edge and the session's program and
 * this screen knows neither; refusing on the client's own scope arithmetic would
 * withhold from a program supervisor an answer about their own worker.
 *
 * The three non-content states of the READ are then kept apart by `listPhase`:
 * a 403 is a SETTLED refusal with no retry button, a 500 or a dropped
 * connection is a retryable failure, and an unarrived answer claims nothing.
 */

/**
 * THE KEY THIS PANEL CHARGES, which is NOT the key the screen around it charges.
 *
 * `GET /location/sessions/:id/policy-binding` is `guard(request, reply,
 * 'time:read')` plus own-worker scoping. Session detail is `admin-views:read`.
 * The two are seeded to different sets of people, so holding one has never
 * implied the other.
 */
export const BINDING_READ_KEY = 'time:read';

export function ShiftPolicyBinding({ sessionId }: { sessionId: string }) {
  const permissions = usePermissions();
  const gate = authorityGate(permissions, BINDING_READ_KEY);

  const query = useQuery({
    queryKey: ['location', 'session-policy-binding', sessionId],
    queryFn: () => location.sessionPolicyBinding(sessionId),
    // `unknown` ASKS. A permission set that could not be read is not a refusal,
    // and the request is the thing that can actually answer — the same rule
    // SessionDetail applies one component up. `unresolved` waits rather than
    // firing a request whose answer would arrive before the question is even
    // settled; `refused` never asks, because it would only ever be a 403.
    enabled: sessionId !== '' && (gateOpen(gate) || gate.kind === 'unknown'),
  });

  if (!gateOpen(gate) && gate.kind !== 'unknown') {
    return (
      <section className="rn-card">
        <h2 className="rn-card-title">What this shift was bound to</h2>
        <GateNotice
          gate={gate}
          what="see the location policy this shift was bound to"
          heading="What this shift was bound to isn’t shown to you"
          detail={
            <>
              Reading the location policy a shift was bound to is part of reading somebody’s time
              records, and it is a separate permission from opening the session itself. Ask your org
              admin for <code>{BINDING_READ_KEY}</code> if you need it.
            </>
          }
          onRetry={permissions.refetch}
        />
      </section>
    );
  }

  return (
    <section className="rn-card">
      <h2 className="rn-card-title">What this shift was bound to</h2>
      <ListState
        phase={listPhase(query)}
        surface="drawer"
        rows={3}
        empty={null}
        failedMessage="We couldn’t read what this shift was bound to just now."
        onRetry={() => void query.refetch()}
        refused={
          <Refused
            heading="This shift’s policy record isn’t yours to read"
            message="You can read the location policy bound to your own shifts and to those of the workers assigned to you. Reading anybody else’s needs an organization-wide timekeeping permission."
          />
        }
      >
        {query.data ? <BindingBody binding={query.data} /> : null}
      </ListState>
    </section>
  );
}

function BindingBody({ binding }: { binding: location.SessionPolicyBinding }) {
  const { disclosed, governed, pending, completeness } = binding;
  return (
    <div className="rn-col" style={{ gap: 'var(--space-16)', marginTop: 'var(--space-10)' }}>
      {/* ─── What the worker was SHOWN. Read from the snapshot, not inferred. ─── */}
      <div>
        <h3 className="rn-card-title" style={{ fontSize: '1rem' }}>
          {disclosureHeading(disclosed.state)}
        </h3>
        <p style={{ margin: 'var(--space-6) 0 0' }}>{disclosureSentence(disclosed)}</p>
        {disclosed.state === 'version' ? (
          <>
            <p className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
              Recorded {formatScheduleInstant(disclosed.effectiveAt)} · began to govern{' '}
              {formatScheduleInstant(disclosed.governsFrom)}
            </p>
            <p className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
              {statedScheduleSentence(
                disclosed.schedule,
                disclosed.schedule.state === 'stated'
                  ? formatScheduleInstant(disclosed.schedule.at)
                  : '',
              )}
            </p>
            {/*
              THE DEAD DATE-ONLY COLUMN, SHOWN AS EVIDENCE AND NEVER AS A
              SCHEDULE. Every version written before migration 1974000000000
              carried an `effective_from` date that deferred nothing; the stated
              dates were kept so a shift that ran under that regime can still be
              asked what its operator typed. It is not an ordering and it did
              not govern, and this line says so rather than printing a date that
              looks like one.
            */}
            {disclosed.legacyStatedEffectiveFrom ? (
              <p className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
                Under the date-only rules in force before this version’s scheme, the operator had
                stated {disclosed.legacyStatedEffectiveFrom}. That date deferred nothing and did not
                govern this shift; it is kept as a record of what was typed.
              </p>
            ) : null}
          </>
        ) : null}
        {disclosed.state !== 'not_recorded' ? <PolicyFacts policy={disclosed.policy} /> : null}
      </div>

      {/* ─── What GOVERNED, in order. Replayed by the server, printed here. ─── */}
      <div>
        <h3 className="rn-card-title" style={{ fontSize: '1rem' }}>
          What governed collection
        </h3>
        <p className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
          {completenessSentence(completeness)}
        </p>
        {/*
          AN EMPTY TIMELINE IS NOT A TIMELINE OF NOTHING. When no binding was
          recorded there is no question this route can answer, and the sentence
          above says so — so the list is simply absent rather than rendered as a
          measured zero. `governed.length === 0` and `completeness === 'unknown'`
          arrive together from the server; this branch reads the ARRAY, so a
          future response that sends one without the other still renders the
          honest thing.
        */}
        {governed.length > 0 ? (
          <ol
            style={{
              listStyle: 'none',
              margin: 'var(--space-10) 0 0',
              padding: 0,
              display: 'grid',
              gap: 'var(--space-12)',
            }}
          >
            {governed.map((segment, i) => (
              <li key={`${segment.from}-${i}`}>
                <Segment segment={segment} />
              </li>
            ))}
          </ol>
        ) : null}
      </div>

      {/* ─── What is COMING, and when it reaches this worker. ─── */}
      <div>
        <h3 className="rn-card-title" style={{ fontSize: '1rem' }}>
          What is still coming
        </h3>
        {pending ? (
          <>
            {/*
              THE SERVER'S OWN SENTENCE. `explanation` names which authority is
              holding the change back, decided by the same function the gate
              uses. Reassembling it here from `waitingOn` and `direction` would
              be a second implementation of the ruling that agrees today.
            */}
            <p style={{ margin: 'var(--space-6) 0 0' }}>{pending.explanation}</p>
            <p className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
              Version {pending.version} · {pending.direction} · published to govern from{' '}
              {formatScheduleInstant(pending.governsFrom)}
            </p>
            {pending.waitingOn.length > 0 ? (
              <p className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
                Waiting on {pending.waitingOn.map(floorLabel).join(' and ')}.
              </p>
            ) : null}
            <p className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
              {pendingArrivalSentence(
                pending.governsForThisWorkerFrom === null
                  ? null
                  : formatScheduleInstant(pending.governsForThisWorkerFrom),
              )}
            </p>
          </>
        ) : (
          <p className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
            Nothing is published and waiting for this worker’s scope. That is an absence measured
            now, not a promise that nothing will change.
          </p>
        )}
      </div>
    </div>
  );
}

function Segment({ segment }: { segment: location.GovernedSegment }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)' }}>
        <strong>
          {formatScheduleInstant(segment.from)} →{' '}
          {segment.to === null ? 'the end of the shift' : formatScheduleInstant(segment.to)}
        </strong>
        {segment.direction ? <Pill variant="plain">{segment.direction}</Pill> : null}
      </div>
      <p className="rn-muted" style={{ margin: 'var(--space-4) 0 0' }}>
        {basisSentence(segment.basis)}
      </p>
      {/*
        The server's OWN explanation of the change, when there was a pair to
        compare. Three of the ten policy axes invert — a shorter breadcrumb
        interval and a tighter accuracy floor are MORE collection, and
        `allow_attested_override: false` is more monitoring — so a screen
        working out which way a change moved would be wrong on a third of them
        and look right.
      */}
      {segment.explanation ? (
        <p className="rn-muted" style={{ margin: 'var(--space-4) 0 0' }}>
          {segment.explanation}
        </p>
      ) : null}
      <PolicyFacts policy={segment.policy} />
    </div>
  );
}

/**
 * The collection rules themselves, stated in the units the server stores them
 * in. Not a comparison and not a verdict — the axes that invert make an
 * unqualified "stricter" or "looser" a claim this screen cannot compute, and
 * the server already supplies the one it can.
 */
function PolicyFacts({ policy }: { policy: location.EffectivePolicy }) {
  return (
    <p className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
      Mode {policy.mode} ·{' '}
      {policy.breadcrumbEnabled
        ? `a location point every ${policy.breadcrumbIntervalSeconds}s while clocked in, kept ${policy.breadcrumbRetentionDays} days`
        : 'no location points collected between punches'}{' '}
      · fixes accepted to {policy.minAccuracyMeters}m ·{' '}
      {policy.allowAttestedOverride
        ? 'the worker may explain an unconfirmed punch'
        : 'an unconfirmed punch is blocked rather than explained'}
    </p>
  );
}
